/**
 * Plan arithmetic, in one place.
 *
 * The product page works out a per-week figure and a saving percentage, and
 * the products grid quotes a "from" price. All three are the same sum over
 * the same prices, and they were drifting: the grid labelled the weekly price
 * "/week and up" when the weekly plan is the most expensive week you can buy,
 * so the cheapest rate on offer was never the one advertised.
 *
 * Everything here reads the prices the page is already charging. Nothing is
 * hardcoded, so a price change in Firestore moves the figures with it.
 */
import type { PlanType } from "../types/games";

/** How many weeks each plan covers. */
export const WEEKS_IN: Partial<Record<PlanType, number>> = {
  weekly: 1,
  monthly: 4,
  quarterly: 13,
  biannual: 26,
};

/** Read a price string like "€10" as a number, or NaN. */
export function amount(price: string): number {
  const v = parseFloat(String(price).replace(/[^0-9.]/g, ""));
  return isFinite(v) ? v : NaN;
}

/** The currency symbol a price string is written with, if any. */
export function symbolOf(price: string): string {
  const head = String(price).trim().charAt(0);
  return /[0-9]/.test(head) ? "" : head;
}

/** Format a number back into a price string, dropping a trailing ".00". */
export function formatPrice(value: number, symbol: string): string {
  return symbol + (value % 1 === 0 ? String(value) : value.toFixed(2));
}

/** What one week of a plan costs, as a number. */
export function perWeekValue(price: string, plan: PlanType): number | null {
  const weeks = WEEKS_IN[plan];
  const v = amount(price);
  if (!weeks || !isFinite(v) || v <= 0) return null;
  return v / weeks;
}

/** What each plan is called in a price line. */
const PLAN_UNIT: Partial<Record<PlanType, string>> = {
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  biannual: "6 months",
  lifetime: "lifetime",
};

/**
 * The cheapest thing a visitor can actually buy, and the period it covers.
 *
 * This deliberately does NOT return the lowest per-week rate. A longer plan
 * works out cheaper per week, but nobody can hand over that amount and get a
 * week -- quoting it as a "from" price advertises something that is not for
 * sale, which is the "$X a month, billed annually" trick. The shortest plan is
 * the real entry price, so that is what a "from" line quotes.
 *
 * The per-week equivalent still has a place: on the plan picker, underneath a
 * plan's own price, where the billing period is named right beside it.
 */
export function entryPrice(
  pricing: Partial<Record<PlanType, string>> | Record<string, string | undefined>
): { display: string; unit: string; plan: PlanType } | null {
  const order: PlanType[] = ["weekly", "monthly", "quarterly", "biannual"];
  for (const plan of order) {
    const price = (pricing as Record<string, string | undefined>)[plan];
    if (typeof price !== "string") continue;
    if (!isFinite(amount(price)) || amount(price) <= 0) continue;
    return { display: price, unit: PLAN_UNIT[plan] ?? plan, plan };
  }
  return null;
}

/**
 * How much cheaper a plan works out per week than the shortest one, as a
 * whole percent. Null unless the plan really is cheaper by at least 5%, so
 * nothing is claimed that the arithmetic does not support.
 */
export function savingPercent(
  price: string,
  plan: PlanType,
  basePrice: string,
  basePlan: PlanType
): number | null {
  const per = perWeekValue(price, plan);
  const basePer = perWeekValue(basePrice, basePlan);
  if (per === null || basePer === null || per >= basePer) return null;
  const pct = Math.round((1 - per / basePer) * 100);
  return pct >= 5 ? pct : null;
}
